import { ReactNode, useState } from 'react'
import { useQuery } from 'react-query'
import { FiInfo } from 'react-icons/fi'
import posthog from 'posthog-js'
import DotsLoading from 'components/loadings/dotsLoading'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { getLoanCandidates, getMemberScore } from 'services/fund'
import { ScoreExplainer, ScoreMeter } from 'components/memberScore'
import { faPlain, longJalali, toman } from 'utilities/helper/format'
import { grantRouteFor, jalaliDateOf } from '../../../../grantFromDemand'
import { longDateOf, timeOf } from '../../../../components/format'

interface IModal {
    show: boolean
    loanId: number
}
interface IDetailProps {
    item: IDemandResult
    isLoading: boolean
    /** from the page, which reads the role off the fund; router state is the fallback */
    isManager?: boolean
    setModal: React.Dispatch<React.SetStateAction<IModal>>
}

function Fact({
    label,
    children,
    wide,
}: {
    label: string
    children: ReactNode
    wide?: boolean
}) {
    return (
        <div className={wide ? ' col-span-2' : ''}>
            <p className=" text-[10px] text-text-muted">{label}</p>
            <div className=" mt-[2px] text-xs font-bold text-text-body">
                {children}
            </div>
        </div>
    )
}

const personName = (person?: IRecomenderAndGuaranter | null) =>
    person?.member
        ? `${person.member.first_name ?? ''} ${
              person.member.last_name ?? ''
          }`.trim()
        : ''

/**
 * The member's numbers, one tap under their request — and what a manager can
 * do about it from the queue.
 *
 * «قبول» now opens the اعطای وام wizard with this member already chosen and
 * the request's amount and instalments prefilled — the same handover the
 * کارتابل's «بررسی و اعطای وام» makes — rather than the old single-form accept
 * screen. It hands over `poolship`, the person the loan is FOR, never
 * `registrar`: on a request a manager filed for somebody else those are two
 * people, and passing the filer is how the accept screen once dead-ended on
 * «شماره حساب را انتخاب کنید» (جابر آزور, fund 39696, request 19944).
 */
export default function Detail({
    item,
    isLoading,
    isManager: isManagerProp,
    setModal,
}: IDetailProps) {
    const navigate = useNavigate()
    const { state } = useLocation()
    const { id: moneypoolId } = useParams()
    const [explaining, setExplaining] = useState(false)
    const poolshipId = item?.poolship?.id
    const isManager = isManagerProp ?? !!state?.isManager

    /**
     * رتبهٔ اعتباری — the ranking the اعطای وام wizard sorts by, in place of
     * the stale `global_credit` this row used to show.
     *
     * A manager reads `loan-candidates`, which ranks the whole fund and is
     * shared (query key and all) with the wizard, so opening several requests
     * costs one scan. A plain member gets that list refused — it carries every
     * member's name, phone number and open request — so they read
     * `member-score`, their own row only, and only when the request is theirs.
     */
    const scoreSource = isManager
        ? 'candidates'
        : item?.is_poolship_requsted_loan
        ? 'own'
        : 'none'
    const candidates = useQuery(
        ['loan-candidates', moneypoolId, true, false],
        getLoanCandidates,
        {
            staleTime: 5 * 60 * 1000,
            enabled: !!moneypoolId && scoreSource === 'candidates',
        }
    )
    const ownScore = useQuery(
        ['member-score', moneypoolId, poolshipId],
        getMemberScore,
        {
            staleTime: 5 * 60 * 1000,
            enabled: !!moneypoolId && !!poolshipId && scoreSource === 'own',
        }
    )
    const scoreRow =
        scoreSource === 'candidates'
            ? candidates.data?.results?.find(
                  (row) => String(row.poolship_id) === String(poolshipId)
              )
            : ownScore.data?.result ?? undefined
    const scoreMeta =
        scoreSource === 'candidates' ? candidates.data : ownScore.data

    const grant = () => {
        posthog.capture('loan_grant_started_from_demand', {
            fund_id: moneypoolId,
            demand_id: item.id,
            prefilled: true,
            source: 'requests_list',
        })
        const route = grantRouteFor(
            moneypoolId ?? '',
            {
                demandId: item.id,
                poolshipId: item.poolship.id,
                memberId: item.poolship.member_id,
                memberName: item.poolship.name,
                amount: item.amount,
                numRepayments: item.num_repayments,
                priority: item.demand_priority,
                description: item.user_description ?? '',
                requestedJalali: jalaliDateOf(item.created),
                cardId: item.selected_card_number ?? null,
            },
            'requests_list',
            state?.name
        )
        navigate(route.to, { state: route.state })
    }

    if (isLoading) return <DotsLoading />

    const guarantor = personName(item?.poolship?.guarantor)
    const recommender = personName(item?.poolship?.recommender)

    return (
        <>
            <div className=" grid grid-cols-2 gap-x-4 gap-y-3 pt-3">
                <Fact label="سرمایهٔ شخصی">
                    {toman(Number(item?.poolship?.capital ?? 0))}
                </Fact>
                <Fact label="بدهی به صندوق">
                    {toman(Number(item?.poolship?.debit_amount ?? 0))}
                </Fact>
                <Fact label="وام‌های گرفته‌شده">
                    {faPlain.format(Number(item?.poolship?.num_loans ?? 0))} وام
                </Fact>
                <Fact label="عضو از">
                    {longJalali(item?.poolship?.jalali_join_date) || '—'}
                </Fact>
                <Fact label="ضامن عضو">{guarantor || 'ثبت نشده'}</Fact>
                <Fact label="معرف عضو">{recommender || 'ثبت نشده'}</Fact>
                {scoreRow && (
                    <Fact label="رتبهٔ اعتباری" wide>
                        <div className=" flex items-center gap-1.5">
                            <span>
                                {faPlain.format(scoreRow.rank)} از{' '}
                                {faPlain.format(scoreMeta?.members_count ?? 0)}{' '}
                                عضو
                            </span>
                            <span
                                role="button"
                                tabIndex={0}
                                aria-label={`توضیح رتبهٔ اعتباری ${scoreRow.member_name}`}
                                onClick={() => setExplaining(true)}
                                onKeyDown={(event) => {
                                    if (
                                        event.key === 'Enter' ||
                                        event.key === ' '
                                    ) {
                                        event.preventDefault()
                                        setExplaining(true)
                                    }
                                }}
                                className=" -m-1 shrink-0 cursor-pointer p-1 font-normal text-text-secondary"
                            >
                                <FiInfo size={14} />
                            </span>
                        </div>
                        {/* «۱۲ از ۵۷۹» and «بالاتر از ۹۷٪» are the same fact
                            only when you do the division in your head */}
                        <div className=" mt-1.5">
                            <ScoreMeter
                                percentile={scoreRow.percentile}
                                size="sm"
                            />
                        </div>
                    </Fact>
                )}
                <Fact label="زمان ثبت درخواست" wide>
                    {longDateOf(item.created)}، ساعت {timeOf(item.created)}
                </Fact>
            </div>

            {isManager ? (
                <div className=" mt-4 flex gap-2">
                    <button
                        type="button"
                        onClick={grant}
                        className=" flex-1 rounded-full bg-green-1 py-2.5 text-sm font-bold text-white"
                    >
                        قبول و اعطای وام
                    </button>
                    <button
                        type="button"
                        onClick={() =>
                            setModal({ show: true, loanId: item.id })
                        }
                        className=" flex-1 rounded-full border border-danger-on bg-white py-2.5 text-sm font-bold text-danger-on"
                    >
                        {/* a manager's own request is theirs to cancel,
                            not to reject */}
                        {item.is_poolship_requsted_loan
                            ? 'لغو درخواست'
                            : 'رد درخواست'}
                    </button>
                </div>
            ) : (
                item.is_poolship_requsted_loan && (
                    <button
                        type="button"
                        onClick={() =>
                            setModal({ show: true, loanId: item.id })
                        }
                        className=" mt-4 w-full rounded-full border border-danger-on bg-white py-2.5 text-sm font-bold text-danger-on"
                    >
                        لغو درخواست
                    </button>
                )
            )}

            {scoreRow && (
                <ScoreExplainer
                    show={explaining}
                    onHide={() => setExplaining(false)}
                    candidate={scoreRow}
                    overdueMultiplier={scoreMeta?.overdue_multiplier ?? 3}
                    earlyMultiplier={scoreMeta?.early_multiplier ?? 3}
                    membersCount={scoreMeta?.members_count ?? 0}
                    inflationApplied={false}
                />
            )}
        </>
    )
}
